/**
 * @file src/lib/hooks/useSmoothScroll.tsx
 * @description This file defines the `useSmoothScroll` custom hook, which provides a reusable function for smoothly scrolling to an HTML element over a specified duration using `framer-motion`. It checks if the element is in the viewport before scrolling. It handles smooth scrolling animations and supports React ref elements.
 * @module useSmoothScroll
 */

import { useRef, useCallback } from 'react';
import { animate } from 'framer-motion';

/**
 * Hook to provide a smooth scrolling function to any HTML element using `framer-motion`.
 * It checks if the element is already in the viewport before scrolling.
 *
 * @returns {Object} An object containing:
 * - `scrollRef`: A `useRef` hook to be applied to the element you want to scroll to.
 * - `smoothScrollTo`: A function to perform smooth scrolling to a target HTML element, only if it's not visible in the viewport.
 */
const useSmoothScroll = () => {
  const scrollRef = useRef<HTMLElement | null>(null);

  /**
   * Checks if an element is visible in the viewport.
   *
   * @param {HTMLElement} element - The target element to check.
   * @returns {boolean} - True if the element is visible in the viewport, false otherwise.
   */
  const isElementInViewport = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  /**
   * Smoothly scrolls the window to the specified element over a given duration using `framer-motion`, only if the element is not already in the viewport.
   *
   * @param {HTMLElement} element - The target element to scroll to.
   * @param {number} duration - The duration (in milliseconds) for the smooth scrolling animation.
   * @param {Function} [callback] - Optional callback to execute after scrolling is complete.
   */
  const smoothScrollTo = useCallback(
    (element: HTMLElement, duration: number, callback?: () => void) => {
      if (isElementInViewport(element)) {
        // If the element is already visible, immediately call the callback
        if (callback) callback();
        return;
      }

      const targetPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      const startPosition = window.pageYOffset;

      animate(startPosition, targetPosition, {
        duration: duration / 1000,
        onUpdate: (latest) => window.scrollTo(0, latest),
        ease: [0.42, 0, 0.58, 1],
        onComplete: () => {
          if (callback) callback();
        },
      });
    },
    [],
  );

  return { scrollRef, smoothScrollTo };
};

export default useSmoothScroll;
